import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  defaultAdminModuleId,
  getAdminModuleById,
  getAdminModules,
} from '@/config/adminModules';
import { routes } from '@/config/routes';
import type { AdminModuleDefinition } from '@/types/adminModule';

interface UseAdminNavigationResult {
  modules: AdminModuleDefinition[];
  selectedModule: AdminModuleDefinition;
  selectedModuleId: string;
  selectModule: (moduleId: string) => void;
}

export function useAdminNavigation(): UseAdminNavigationResult {
  const navigate = useNavigate();
  const { moduleId } = useParams<{ moduleId?: string }>();
  const modules = useMemo(() => getAdminModules(), []);
  const hasRequestedModule = Boolean(moduleId && getAdminModuleById(moduleId));
  const selectedModuleId = useMemo(() => {
    if (moduleId && getAdminModuleById(moduleId)) {
      return moduleId;
    }

    return defaultAdminModuleId;
  }, [moduleId]);

  const selectedModule = useMemo(() => {
    return (
      getAdminModuleById(selectedModuleId) ??
      getAdminModuleById(defaultAdminModuleId) ??
      modules[0]
    );
  }, [modules, selectedModuleId]);

  useEffect(() => {
    if (moduleId && !hasRequestedModule) {
      navigate(routes.adminModule(defaultAdminModuleId), { replace: true });
    }
  }, [hasRequestedModule, moduleId, navigate]);

  const selectModule = useCallback(
    (moduleId: string) => {
      if (moduleId === selectedModuleId) {
        return;
      }

      navigate(routes.adminModule(moduleId));
    },
    [navigate, selectedModuleId],
  );

  return {
    modules,
    selectedModule,
    selectedModuleId,
    selectModule,
  };
}
